import { useEffect, useState } from 'react'
import {
  BrowserRouter, Navigate, NavLink, Outlet, Route, Routes, useNavigate,
} from 'react-router-dom'
import { auth } from './api/client'
import { PreferencesProvider } from './hooks/usePreferences'
import { Admin } from './pages/Admin'
import { Alerts } from './pages/Alerts'
import { Login } from './pages/Login'
import { Overview } from './pages/Overview'
import { TargetDetailPage } from './pages/TargetDetail'
import { Trends } from './pages/Trends'

type Theme = 'light' | 'dark' | 'system'
const THEME_KEY = 'tmmon.theme'

export function App() {
  return (
    <BrowserRouter>
      <PreferencesProvider>
      <Routes>
        <Route path="/login" element={<Login />} />
        <Route element={<Shell />}>
          <Route path="/" element={<Guard><Overview /></Guard>} />
          <Route path="/targets/:id" element={<Guard><TargetDetailPage /></Guard>} />
          <Route path="/alerts" element={<Guard><Alerts /></Guard>} />
          <Route path="/trends" element={<Guard><Trends /></Guard>} />
          <Route path="/admin" element={<Guard><Admin /></Guard>} />
          <Route path="*" element={<Navigate to="/" replace />} />
        </Route>
      </Routes>
      </PreferencesProvider>
    </BrowserRouter>
  )
}

/** Bounce to login when there is no usable token. */
function Guard({ children }: { children: React.ReactNode }) {
  if (!auth.isValid()) return <Navigate to="/login" replace />
  return <>{children}</>
}

function Shell() {
  const navigate = useNavigate()
  const [theme, setTheme] = useState<Theme>(
    () => (localStorage.getItem(THEME_KEY) as Theme) || 'system')

  useEffect(() => {
    const root = document.documentElement
    if (theme === 'system') root.removeAttribute('data-theme')
    else root.setAttribute('data-theme', theme)
    localStorage.setItem(THEME_KEY, theme)
  }, [theme])

  // The API dispatches this when a request comes back 401, so an expired
  // session takes the user to the login page instead of leaving a dead screen.
  useEffect(() => {
    const onUnauthorized = () => navigate('/login', { replace: true })
    window.addEventListener('tmmon:unauthorized', onUnauthorized)
    return () => window.removeEventListener('tmmon:unauthorized', onUnauthorized)
  }, [navigate])

  const user = auth.user()

  return (
    <>
      <header className="app-header">
        <NavLink to="/" className="app-brand">TruckManager Monitor</NavLink>
        <nav className="app-nav">
          <NavLink to="/" end>Overview</NavLink>
          <NavLink to="/alerts">Alerts</NavLink>
          <NavLink to="/trends">Trends</NavLink>
          <NavLink to="/admin">Admin</NavLink>
        </nav>

        <button
          type="button"
          onClick={() => setTheme(theme === 'dark' ? 'light' : theme === 'light' ? 'system' : 'dark')}
          title={
            theme === 'system'
              ? 'Theme: Auto — follows your operating system. Click for Dark.'
              : `Theme: ${theme === 'dark' ? 'Dark' : 'Light'}. Click to change.`
          }
          aria-label={`Theme: ${theme === 'system' ? 'Auto' : theme}. Click to change.`}
        >
          <span className="muted" style={{ fontWeight: 400 }}>Theme:</span>{' '}
          {theme === 'dark' ? 'Dark' : theme === 'light' ? 'Light' : 'Auto'}
        </button>

        {user && (
          <button
            type="button"
            onClick={() => { auth.clear(); navigate('/login', { replace: true }) }}
          >
            Sign out {user.displayName ?? user.username}
          </button>
        )}
      </header>

      <main className="app-main">
        <Outlet />
      </main>
    </>
  )
}
